/**
 * The inspector's facets, as a rail instead of a scroll.
 *
 * A step carries five different kinds of fact — what it is, what the operator does, what it
 * records, who performs and signs it, what it controls. Stacked vertically they are a column
 * nobody reaches the bottom of, and the fact that matters most is as likely to be off-screen
 * as not. One facet is open at a time and the rail says what the others hold.
 *
 * **A badge is a fact, not decoration.** It carries a count the caller derived from the
 * record, or a gap the checks found — so the rail shows where the missing role is without
 * anyone opening four panes to find it. `tone` is the same vocabulary the findings use:
 * `block` for something that stops the record, `warn` for something advisory, `plain` for a
 * count that is merely useful.
 *
 * Generic on purpose. The operation inspector has the same problem and should not grow a
 * second rail to solve it (U0 §1 — one component per concept).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Tooltip } from "antd";

const POS_KEY = "u0.rail.pos";
const readPos = () => { try { return JSON.parse(localStorage.getItem(POS_KEY) || "null"); } catch { return null; } };
const writePos = (p) => { try { p ? localStorage.setItem(POS_KEY, JSON.stringify(p)) : localStorage.removeItem(POS_KEY); } catch { /* private window */ } };

// 2026-10-28 (owner) · a placed rail is held from its NEARER edges (right/bottom when it sits in
// that half of the screen), so the hover magnification grows it toward the middle — it can be
// put flush against any edge and still never grow past one. Stored as { ax, x, ay, y }: which
// edge, and how far from it. An older { x, y } is a left/top position.
function toBox(p, w, h) {
  if (!p.ax) return { left: p.x, top: p.y };
  return {
    left: p.ax === "r" ? window.innerWidth - p.x - w : p.x,
    top: p.ay === "b" ? window.innerHeight - p.y - h : p.y,
  };
}

/** Anchor a rail whose box is at (left, top), kept on screen whatever the window did since. */
function anchor(left, top, w, h) {
  const W = window.innerWidth, H = window.innerHeight;
  const l = Math.max(4, Math.min(left, W - w - 4)), t = Math.max(4, Math.min(top, H - h - 4));
  const right = l + w / 2 > W / 2, bottom = t + h / 2 > H / 2;
  return { ax: right ? "r" : "l", x: right ? W - l - w : l, ay: bottom ? "b" : "t", y: bottom ? H - t - h : t };
}

/** The inline position: the anchored edges set, the other two released. */
function placeStyle(p) {
  return {
    left: p.ax === "r" ? "auto" : p.x, right: p.ax === "r" ? p.x : "auto",
    top: p.ay === "b" ? "auto" : p.y, bottom: p.ay === "b" ? p.y : "auto",
  };
}

export default function InspectorRail({ items = [], active, onChange, docked = false }) {
  const shown = items.filter(Boolean);
  // 2026-09-28 · the rail floats over the page and can be moved: dragged by its grip, placed
  // where it is out of the way, remembered per viewer. The inspector no longer keeps a strip
  // clear for it, so the pane has the whole width. Double-click the grip to put it back.
  // 2026-10-28 (owner) · dragged from anywhere on it, not only the grip: a press that moves
  // more than a few pixels is a drag, and the button under it is not pressed.
  const ref = useRef(null);
  const [pos, setPos] = useState(readPos);
  const [live, setLive] = useState(null);    // { left, top } while a drag is under way
  useEffect(() => {
    const fit = () => setPos((p) => {
      const el = ref.current;
      if (!p || !el) return p;
      const w = el.offsetWidth, h = el.offsetHeight, b = toBox(p, w, h);
      return anchor(b.left, b.top, w, h);
    });
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, []);
  const onDown = useCallback((e) => {
    if (e.button !== 0) return;
    const r = ref.current.getBoundingClientRect();
    const d = { x0: e.clientX, y0: e.clientY, dx: e.clientX - r.left, dy: e.clientY - r.top, on: false, at: null };
    const move = (ev) => {
      if (!d.on && Math.hypot(ev.clientX - d.x0, ev.clientY - d.y0) < 5) return;
      d.on = true;
      const el = ref.current;
      d.at = {
        left: Math.max(4, Math.min(ev.clientX - d.dx, window.innerWidth - el.offsetWidth - 4)),
        top: Math.max(4, Math.min(ev.clientY - d.dy, window.innerHeight - el.offsetHeight - 4)),
      };
      setLive(d.at);
    };
    const up = () => {
      window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up);
      if (!d.on) return;
      // The press was a drag: the click it ends in must not press the button it started on.
      const eat = (ev) => { ev.stopPropagation(); ev.preventDefault(); };
      window.addEventListener("click", eat, { capture: true, once: true });
      setTimeout(() => window.removeEventListener("click", eat, { capture: true }), 0);
      const el = ref.current;
      const p = anchor(d.at.left, d.at.top, el.offsetWidth, el.offsetHeight);
      writePos(p); setPos(p); setLive(null);
    };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
  }, []);
  const reset = () => { writePos(null); setPos(null); };
  if (shown.length < 2) return null;

  // 2026-10-14 (step panel redesign) · docked: a row in the record's header, not a toolbar
  // floating over the step panel's fields. Same tabs, same names, same actions.
  return (
    <div ref={ref} className={`u0-rail ${docked ? "docked" : live || pos ? "placed" : ""} ${live ? "dragging" : ""}`}
         role="tablist" aria-orientation={docked ? "horizontal" : "vertical"}
         onPointerDown={docked ? undefined : onDown}
         style={docked ? undefined : live ? { left: live.left, top: live.top, right: "auto", bottom: "auto" }
           : pos ? (pos.ax ? placeStyle(pos) : { left: pos.x, top: pos.y }) : undefined}>
      {docked ? null : (
        <Tooltip title="Drag anywhere on the toolbar to move it · double-click here to put it back" placement="left">
          <div className="u0-railgrip" onDoubleClick={reset} aria-label="Move the toolbar">⋮⋮</div>
        </Tooltip>
      )}
      {shown.map((item) => {
        const on = item.key === active;
        const Icon = item.icon;
        return (
          <Tooltip key={item.key} title={item.label} placement={docked ? "bottom" : "left"}>
            <button
              role="tab"
              aria-selected={on}
              aria-label={item.label}
              className={`u0-railbtn ${on ? "on" : ""}`}
              onClick={() => onChange?.(item.key)}
            >
              <Icon size={16} strokeWidth={1.75} />
              {item.badge ? (
                <span className={`u0-raildot ${item.tone || "plain"}`}>
                  {item.badge === true ? "" : item.badge}
                </span>
              ) : null}
            </button>
          </Tooltip>
        );
      })}
    </div>
  );
}
